import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Fab,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PropTypes from 'prop-types';
import UserAuthentication from '../../../components/userAuthentication';
import AestheticProtocolModal from './aestheticProtocolModal';
import AestheticalProtocolProcedures from './aestheticalProtocolProcedures';
import Loading from '../../../components/loading';
import ErrorMessage from '../../../components/errorMessage';
import SuccessMessage from '../../../components/successMessage';
import { useAestheticProtocols } from './hooks/useAestheticProtocols';

export default function AestheticProtocolMobile({ patientId }) {
  const [isLoading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    protocols,
    isLoading: isProtocolsLoading,
    error: listError,
    refetch: fetchProtocols,
  } = useAestheticProtocols(patientId);

  useEffect(() => {
    if (listError) {
      setShowErrorMessage(true);
      setErrorMessage(listError);
    }
  }, [listError]);

  const handleSave = () => {
    setIsModalOpen(false);
    fetchProtocols();
  };

  return (
    <>
      <UserAuthentication />
      <Loading isToHidden={isLoading === false && isProtocolsLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Box>
        {selectedProtocol ? (
          <Box>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => setSelectedProtocol(null)}
              sx={{ mb: 1 }}
            >
              Voltar aos Protocolos
            </Button>
            <AestheticalProtocolProcedures
              baseUrl={`/aesthetic-protocols/${selectedProtocol.id}/procedures`}
              subtitle={selectedProtocol.description}
              showCount={false}
              isMobile
            />
          </Box>
        ) : (
          <>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardHeader
                title={
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Protocolos
                  </Typography>
                }
                action={
                  <Fab
                    color="primary"
                    size="small"
                    aria-label="adicionar protocolo"
                    sx={{ boxShadow: 2 }}
                    onClick={() => setIsModalOpen(true)}
                  >
                    <AddIcon />
                  </Fab>
                }
              />
              <CardContent>
                {protocols.length === 0 ? (
                  <Typography align="center" sx={{ py: 4 }}>
                    Nenhum protocolo estético cadastrado ainda.
                  </Typography>
                ) : (
                  <List>
                    {protocols.map((protocol) => (
                      <React.Fragment key={protocol.id}>
                        <ListItem
                          onClick={() => setSelectedProtocol(protocol)}
                          sx={{ cursor: 'pointer' }}
                          secondaryAction={
                            <Chip label={protocol.status} color="info" size="small" />
                          }
                        >
                          <ListItemText
                            primary={protocol.description}
                            secondary={`${protocol.professionalName} · ${protocol.initiatedAt} · ${protocol.totalAmountFormatted}`}
                            primaryTypographyProps={{ fontWeight: 'bold', mb: 0.5 }}
                          />
                        </ListItem>
                        <Divider component="li" />
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
            <Box sx={{ mt: 2 }}>
              <AestheticalProtocolProcedures
                baseUrl={patientId ? `/patients/${patientId}/aesthetic-avulsion-procedures` : null}
                applicationsResource="/aesthetic-avulsion-procedures"
                title="Procedimentos Avulsos"
                subtitle="Atendimento rápido sem vínculo com protocolo"
                isMobile
              />
            </Box>
          </>
        )}
      </Box>

      <AestheticProtocolModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        patientId={patientId}
      />
    </>
  );
}

AestheticProtocolMobile.propTypes = {
  patientId: PropTypes.string,
};

AestheticProtocolMobile.defaultProps = {
  patientId: null,
};
